import { NextUIProvider } from '@nextui-org/system';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from 'next-themes';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import * as Sentry from '@sentry/react';
import { GraphqlProvider } from '@/src/lib/graphql/provider';
import { CampaignEditingProvider } from '@/src/contexts/campaign-editing-context';
import { OneSignalProvider } from '@/src/components/one-signal-provider';
import HttpInterceptor from '@/src/components/http-interceptor';
import { createClicksterStore, useClicksterStore } from '@/src/stores';
import { AppRoutes } from './routes';
import { ErrorFallback } from '@/src/components/error-fallback';

// Initialize store (idempotent)
createClicksterStore();

export function App() {
  const [queryClient] = useState(
    () => new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } })
  );
  const navigate = useNavigate();
  const hydrate = useClicksterStore((state) => state.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} resetError={resetError} />
      )}
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        <NextUIProvider navigate={navigate}>
          <ThemeProvider attribute="class" defaultTheme="dark">
            <Toaster richColors position="top-right" style={{ top: '80px' }} />
            <GraphqlProvider>
              <CampaignEditingProvider>
                <OneSignalProvider>
                  <HttpInterceptor />
                  <main className="flex-1">
                    <AppRoutes />
                  </main>
                </OneSignalProvider>
              </CampaignEditingProvider>
            </GraphqlProvider>
          </ThemeProvider>
        </NextUIProvider>
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
